<template>
    <Teleport to="body">
        <Transition name="modal">
            <div v-if="isOpen" class="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
                <!-- Backdrop -->
                <div class="absolute inset-0 bg-black/70 backdrop-blur-sm" @click="$emit('close')" />

                <div
                    ref="modalRef"
                    class="relative w-full max-w-md max-h-[100dvh] sm:max-h-[90vh] flex flex-col bg-zinc-950 rounded-t-3xl sm:rounded-2xl border-t border-white/10 sm:border border-white/10 shadow-2xl"
                    :style="modalStyle"
                    @touchstart="handleTouchStart"
                    @touchmove="handleTouchMove"
                    @touchend="handleTouchEnd"
                >
                    <!-- Mobile Pull Handle -->
                    <div class="sm:hidden flex justify-center pt-2 pb-0.5 cursor-grab active:cursor-grabbing">
                        <div class="w-10 h-1 rounded-full bg-zinc-700" />
                    </div>

                    <!-- Close Button -->
                    <button
                        @click="$emit('close')"
                        class="absolute top-2.5 right-2.5 sm:top-3 sm:right-3 z-10 w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center transition-colors active:scale-95"
                        aria-label="Close"
                    >
                        <X class="w-4 h-4 text-white/70" />
                    </button>

                    <div class="px-5 pt-6 pb-5 sm:px-6 sm:pt-8 sm:pb-6">
                        <!-- Icon & Title -->
                        <div class="flex flex-col items-center mb-5">
                            <div class="w-16 h-16 rounded-full bg-blue-500/15 border-2 border-blue-500/25 flex items-center justify-center mb-4 icon-pop">
                                <MessageCircle class="w-8 h-8 text-blue-400" />
                            </div>

                            <h2 class="text-white text-xl font-bold mb-2 text-center">
                                Send Your First Message
                            </h2>

                            <p class="text-white/60 text-center text-sm leading-relaxed max-w-xs">
                                Your stars just made a difference! Drop a quick note to
                                <span class="text-blue-400 font-semibold">{{ performerName }}</span>
                                — performers love hearing from their fans.
                            </p>
                        </div>

                        <!-- Comment Input -->
                        <div class="mb-4">
                            <textarea
                                ref="textareaRef"
                                v-model="commentText"
                                placeholder="&quot;This audition was 🔥&quot;  &quot;Can't wait for the full scene!&quot;"
                                rows="3"
                                maxlength="500"
                                class="w-full px-4 py-3 rounded-xl bg-zinc-900 border border-white/10 text-white text-sm placeholder:text-white/30 focus:border-blue-500 focus:outline-none transition-colors resize-none"
                            />
                            <div class="flex justify-end mt-1">
                                <span class="text-[11px]" :class="commentText.length > 0 ? 'text-white/40' : 'text-transparent'">
                                    {{ commentText.length }}/500
                                </span>
                            </div>
                        </div>

                        <!-- Actions -->
                        <div class="space-y-2.5">
                            <button
                                @click="handleSubmit"
                                :disabled="!commentText.trim()"
                                :class="[
                                    'w-full py-3 font-semibold rounded-xl text-sm transition-all flex items-center justify-center gap-2',
                                    commentText.trim()
                                        ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/25 active:scale-95'
                                        : 'bg-zinc-800 text-white/30 cursor-not-allowed'
                                ]"
                            >
                                <Send class="w-4 h-4" />
                                Send Message
                            </button>

                            <button
                                @click="$emit('maybeLater')"
                                class="w-full py-2.5 text-white/50 hover:text-white/70 text-sm font-medium transition-colors"
                            >
                                Maybe Next Time
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { X, MessageCircle, Send } from 'lucide-vue-next'

const props = defineProps({
    isOpen: {
        type: Boolean,
        required: true,
    },
    audition: {
        type: Object,
        default: null,
    },
})

const emit = defineEmits(['close', 'submit', 'maybeLater'])

const commentText = ref('')
const textareaRef = ref(null)

const performerName = computed(() => props.audition?.creator?.name ?? 'the performer')

watch(() => props.isOpen, (open) => {
    if (open) {
        commentText.value = ''
        nextTick(() => textareaRef.value?.focus())
    }
})

const handleSubmit = () => {
    if (!commentText.value.trim()) return
    emit('submit', commentText.value)
}

// Touch/swipe-to-dismiss
const modalRef = ref(null)
const touchStartY = ref(0)
const touchCurrentY = ref(0)
const isDragging = ref(false)

const modalStyle = computed(() => {
    if (!isDragging.value) return {}
    const deltaY = touchCurrentY.value - touchStartY.value
    if (deltaY < 0) return {}
    return { transform: `translateY(${deltaY}px)`, transition: 'none' }
})

const handleTouchStart = (e) => {
    if (!modalRef.value) return
    const touch = e.touches[0]
    const rect = modalRef.value.getBoundingClientRect()
    if (touch.clientY - rect.top > 60) return
    touchStartY.value = touch.clientY
    touchCurrentY.value = touch.clientY
    isDragging.value = true
}

const handleTouchMove = (e) => {
    if (!isDragging.value) return
    touchCurrentY.value = e.touches[0].clientY
    if (touchCurrentY.value - touchStartY.value > 0) e.preventDefault()
}

const handleTouchEnd = () => {
    if (!isDragging.value) return
    if (touchCurrentY.value - touchStartY.value > 100) emit('close')
    isDragging.value = false
    touchStartY.value = 0
    touchCurrentY.value = 0
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

@keyframes icon-pop {
    0%   { transform: scale(0.3) rotate(-15deg); opacity: 0; }
    60%  { transform: scale(1.15) rotate(4deg); }
    100% { transform: scale(1) rotate(0deg); opacity: 1; }
}

.icon-pop {
    animation: icon-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.15s both;
}
</style>
